/**
 * Reguły strefy alarmowej — jedno miejsce dla całej aplikacji.
 *
 * Wcześniej te same warunki żyły w kopiach w każdym komponencie, który edytował
 * strefę, i to właśnie pozwoliło rozejść się błędowi zapisującemu punkt 0,0.
 * Tu nie ma zależności od Angulara ani od Google Maps — czysta domena.
 */

export interface ZoneLocation {
  address: string;
  latitude: number;
  longitude: number;
}

export interface AlarmZone extends ZoneLocation {
  radius: number;
  interval: number;
}

/** Zakresy jak w suwakach UI. Backend ich nie waliduje — jego DTO nigdy nie działało. */
export const ZONE_RADIUS_MIN = 200;
export const ZONE_RADIUS_MAX = 5000;
export const ZONE_INTERVAL_MIN = 15;
export const ZONE_INTERVAL_MAX = 240;
/** Szerokość kolumny alarm_zone.address. */
export const ZONE_ADDRESS_MAX_LENGTH = 255;

export function normalizeAddress(value: string): string {
  return (value || '').trim();
}

/**
 * Brakiem współrzędnych jest wyłącznie null/pusta/nieliczbowa wartość.
 * Zero jest poprawną współrzędną — południk zerowy przechodzi przez Hiszpanię,
 * Francję i Wielką Brytanię — i nie jest tu w żaden sposób wyróżniane.
 */
export function hasCoordinates(latitude: any, longitude: any): boolean {
  return isFinite(parseFloat(latitude)) && isFinite(parseFloat(longitude));
}

export function clampToRange(value: any, min: number, max: number, fallback: number): number {
  const num = parseFloat(value);
  if (!isFinite(num)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, Math.round(num)));
}

/**
 * Buduje obiekt strefy do wysyłki. Zawsze komplet pięciu pól: PATCH jest zapisem
 * częściowym, więc adres bez współrzędnych zostawiłby geofence w poprzednim
 * miejscu bez żadnego widocznego objawu.
 */
export function buildAlarmZonePayload(raw: any): AlarmZone {
  return {
    address: normalizeAddress(raw.address),
    radius: clampToRange(raw.radius, ZONE_RADIUS_MIN, ZONE_RADIUS_MAX, ZONE_RADIUS_MIN),
    interval: clampToRange(raw.interval, ZONE_INTERVAL_MIN, ZONE_INTERVAL_MAX, ZONE_INTERVAL_MIN),
    latitude: parseFloat(raw.latitude),
    longitude: parseFloat(raw.longitude)
  };
}

/** Komunikaty walidacyjne strefy zwrócone przez backend, spłaszczone do listy. */
export function extractZoneErrors(err: any): string[] {
  const errors = err ? err.errors : null;
  if (!errors || Array.isArray(errors) || !errors.zone) {
    return [];
  }
  return Object.keys(errors.zone).reduce((acc: string[], field: string) => {
    const messages = errors.zone[field];
    if (Array.isArray(messages)) {
      return acc.concat(messages);
    }
    return messages ? acc.concat([String(messages)]) : acc;
  }, []);
}
